(() => {
  'use strict';

  if (customElements.get('pt-visual-grid')) return;

  const sanitizeClone = (node) => {
    const clone = node.cloneNode(true);
    if (clone.nodeType === Node.ELEMENT_NODE) clone.removeAttribute('id');
    clone.querySelectorAll?.('[id]').forEach((element) => element.removeAttribute('id'));
    clone.querySelectorAll?.('[for], [aria-labelledby], [aria-describedby], [aria-controls]').forEach((element) => {
      element.removeAttribute('for');
      element.removeAttribute('aria-labelledby');
      element.removeAttribute('aria-describedby');
      element.removeAttribute('aria-controls');
    });
    return clone;
  };

  class VisualGrid extends HTMLElement {
    connectedCallback() {
      this.abort?.abort();
      this.abort = new AbortController();
      this.cards = Array.from(this.querySelectorAll(':scope > .pt-visual-card')).filter((card) =>
        card.querySelector(':scope > .pt-visual-card__media > img, :scope > .pt-visual-card__media > [data-visual-open] > img')
      );
      this.enhanceCards();
    }

    disconnectedCallback() {
      this.abort?.abort();
      if (this.dialog?.open) this.dialog.close();
      this.dialog?.remove();
      this.dialog = null;
      this.opener = null;
    }

    enhanceCards() {
      this.cards.forEach((card, index) => {
        const figure = card.querySelector(':scope > .pt-visual-card__media');
        let button = figure?.querySelector(':scope > [data-visual-open]');
        const image = button?.querySelector(':scope > img') || figure?.querySelector(':scope > img');
        if (!figure || !image) return;
        if (!button) {
          button = document.createElement('button');
          button.type = 'button';
          button.setAttribute('aria-haspopup', 'dialog');
          button.dataset.visualOpen = String(index);
          image.before(button);
          button.append(image);
        }
        button.setAttribute('aria-label', `Apri ${this.cardTitle(card, image)}`);
        button.addEventListener('click', () => this.open(index, button), { signal: this.abort.signal });
      });
    }

    cardTitle(card, image) {
      return card.querySelector(':scope > .pt-visual-card__copy h3')?.textContent.trim() || image.alt.trim() || 'immagine';
    }

    open(index, opener) {
      if (!window.HTMLDialogElement) return;
      this.opener = opener;
      opener.setAttribute('aria-expanded', 'true');
      this.activeIndex = index;
      this.dialog?.remove();
      this.dialog = this.buildDialog();
      document.body.append(this.dialog);
      this.renderDialogCard();
      this.dialog.showModal();
      this.dialog.querySelector('[data-visual-close]').focus({ preventScroll: true });
    }

    buildDialog() {
      const dialog = document.createElement('dialog');
      dialog.className = 'pt-visual-dialog';
      dialog.setAttribute('aria-label', 'Dettaglio immagine');
      const shell = document.createElement('div');
      shell.className = 'pt-visual-dialog__shell';
      const close = document.createElement('button');
      close.type = 'button';
      close.className = 'pt-visual-dialog__close';
      close.dataset.visualClose = '';
      close.textContent = 'Chiudi';
      close.setAttribute('aria-label', 'Chiudi dettaglio immagine');
      const content = document.createElement('div');
      content.className = 'pt-visual-dialog__content';
      content.dataset.visualContent = '';
      const controls = document.createElement('div');
      controls.className = 'pt-visual-dialog__controls';
      const previous = document.createElement('button');
      previous.type = 'button';
      previous.dataset.visualPrevious = '';
      previous.textContent = 'Precedente';
      const position = document.createElement('p');
      position.className = 'pt-visual-dialog__position';
      position.dataset.visualPosition = '';
      position.setAttribute('aria-live', 'polite');
      const next = document.createElement('button');
      next.type = 'button';
      next.dataset.visualNext = '';
      next.textContent = 'Successiva';
      controls.append(previous, position, next);
      shell.append(close, content, controls);
      dialog.append(shell);

      close.addEventListener('click', () => dialog.close());
      previous.addEventListener('click', () => this.move(-1));
      next.addEventListener('click', () => this.move(1));
      dialog.addEventListener('click', (event) => { if (event.target === dialog) dialog.close(); });
      dialog.addEventListener('cancel', () => dialog.close());
      dialog.addEventListener('close', () => {
        const opener = this.opener;
        dialog.remove();
        if (this.dialog === dialog) this.dialog = null;
        if (opener?.isConnected) { opener.setAttribute('aria-expanded', 'false'); opener.focus({ preventScroll: true }); }
      }, { once: true });
      return dialog;
    }

    move(direction) {
      this.activeIndex = (this.activeIndex + direction + this.cards.length) % this.cards.length;
      this.renderDialogCard();
    }

    renderDialogCard() {
      const card = this.cards[this.activeIndex];
      const source = card.querySelector(':scope > .pt-visual-card__media img');
      const copy = card.querySelector(':scope > .pt-visual-card__copy');
      const content = this.dialog.querySelector('[data-visual-content]');
      const position = this.dialog.querySelector('[data-visual-position]');
      const previous = this.dialog.querySelector('[data-visual-previous]');
      const next = this.dialog.querySelector('[data-visual-next]');
      const image = document.createElement('img');
      image.src = source.currentSrc || source.src;
      image.alt = source.alt;
      if (source.width) image.width = source.width;
      if (source.height) image.height = source.height;
      content.replaceChildren(image, ...(copy ? [sanitizeClone(copy)] : []));
      this.dialog.setAttribute('aria-label', this.cardTitle(card, source));
      position.textContent = `${this.activeIndex + 1} di ${this.cards.length}: ${this.cardTitle(card, source)}`;
      previous.disabled = this.cards.length < 2;
      next.disabled = this.cards.length < 2;
    }
  }

  customElements.define('pt-visual-grid', VisualGrid);
})();
